import React from 'react';
import { IconCheck, IconUtensils, IconPackage, IconTruck, IconMapPin } from './Icons';
import {
  ORDER_TRACKING_FLOW,
  PICKUP_TRACKING_FLOW,
  collectStatusTimestamps,
  humanOrderStatus,
  isPickupOrder,
} from '../lib/orders';
import { formatTimeShort, isValidDate } from '../lib/format';

const STEP_ICONS = {
  CONFIRMED: IconCheck,
  PREPARING: IconUtensils,
  READY_FOR_PICKUP: IconPackage,
  ON_THE_WAY: IconTruck,
  DELIVERED: IconMapPin,
};

export function OrderTrackingTimeline({ order }) {
  const status = String(order?.status || '').toUpperCase();
  const ts = collectStatusTimestamps(order);
  const isPickup = isPickupOrder(order);
  const flow = isPickup ? PICKUP_TRACKING_FLOW : ORDER_TRACKING_FLOW;

  function stepVisualState(orderStatus, stepIndex) {
    const s = String(orderStatus || '').toUpperCase();
    if (s === 'CANCELLED') return 'cancelled';
    if (s === 'DELIVERED' || s === 'REFUNDED') return 'completed';
    const p = flow.indexOf(s);
    if (p < 0) return 'pending';
    if (p > stepIndex) return 'completed';
    if (p === stepIndex) return 'current';
    return 'pending';
  }

  return (
    <div className="order-tracking-timeline">
      {status === 'REFUNDED' ? (
        <div className="alert alert-warning border-0 mb-3 mb-md-4" role="status">
          <strong>Refunded.</strong> Payment was returned after delivery; fulfillment steps below reflect the last known
          journey.
        </div>
      ) : null}
      {status === 'CANCELLED' ? (
        <div className="alert alert-danger border-0 mb-3 mb-md-4" role="alert">
          <strong>Cancelled.</strong> No further fulfillment steps apply.
        </div>
      ) : null}

      <ul className="order-tracking-timeline__list list-unstyled mb-0">
        {flow.map((step, i) => {
          const visual = stepVisualState(status, i);
          const Icon = STEP_ICONS[step] || IconMapPin;
          const when = ts.get(step);
          const validWhen = isValidDate(when) ? when : undefined;
          const timeLabel = validWhen ? formatTimeShort(validWhen) : '';
          const isLast = i === flow.length - 1;
          const showProgressHint = visual === 'current' && step === 'PREPARING';

          const title =
            step === 'DELIVERED' && isPickup
              ? 'Picked up / Handed over'
              : step === 'READY_FOR_PICKUP' && isPickup
                ? 'Ready for counter pickup'
                : humanOrderStatus(step);

          return (
            <li key={step} className={`order-tracking-timeline__item order-tracking-timeline__item--${visual}`}>
              <div className="order-tracking-timeline__rail" aria-hidden={isLast}>
                {!isLast ? <span className="order-tracking-timeline__line" /> : null}
                <span className="order-tracking-timeline__dot">
                  <span className="order-tracking-timeline__dot-inner">
                    <Icon size={16} strokeWidth={2} />
                  </span>
                </span>
              </div>
              <div className="order-tracking-timeline__body">
                <div className="order-tracking-timeline__title-row">
                  <span className="order-tracking-timeline__title">{title}</span>
                  {timeLabel && validWhen ? (
                    <time className="order-tracking-timeline__time tabular-nums" dateTime={validWhen}>
                      {timeLabel}
                    </time>
                  ) : null}
                </div>
                {showProgressHint ? (
                  <p className="order-tracking-timeline__hint mb-0">In progress…</p>
                ) : visual === 'current' && step === 'READY_FOR_PICKUP' && isPickup ? (
                  <p className="order-tracking-timeline__hint mb-0 text-success small fw-semibold">
                    Ready at counter for customer pickup
                  </p>
                ) : visual === 'current' && step !== 'DELIVERED' ? (
                  <p className="order-tracking-timeline__hint mb-0 text-secondary small">Current stage</p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
